import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, FileText, Shield, UserCog, Search, Mail, 
  ArrowLeft, AlertCircle, CheckCircle, Download, 
  ChevronLeft, ChevronRight, Filter, Building2, RefreshCw, Trash2, AlertTriangle,
  Database, HardDrive, BarChart3, Settings, MessageSquare, Star, Send, Lock, Plus,
  Code2, Copy, Check, Globe, Key
} from 'lucide-react';
import LevelUpHeader from '../components/LevelUpHeader';
import SunsetBanner from '../components/SunsetBanner';
import LevelUpFooter from '../components/LevelUpFooter';
import TaxonomyManager from '../components/TaxonomyManager';

function Admin({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // User management states
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [ndaFilter, setNdaFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const pageSize = 25;
  
  // User lookup states - for assigning roles to specific users
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [editingRoles, setEditingRoles] = useState(false);
  const [totalAssets, setTotalAssets] = useState(0);
  
  // User deletion states
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Database export states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [includePasswords, setIncludePasswords] = useState(false);

  // Feedback states
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('');
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('hide_resolved');

  // Reporting states
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  // Approval request states (Task 1)
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('pending');
  const [approvalTypeFilter, setApprovalTypeFilter] = useState('');  // '' = all, 'admin_access', 'upload_access'

  // Derived: is the logged-in user a report_viewer (but not admin)?
  const isReportViewerOnly = (user?.roles || []).includes('report_viewer') && !(user?.roles || []).includes('admin');

  // Access rules states (Task 2)
  const [accessRules, setAccessRules] = useState([]);
  const [accessRulesLoading, setAccessRulesLoading] = useState(false);
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [blockedCountriesLoading, setBlockedCountriesLoading] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [copiedBlock, setCopiedBlock] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '', description: '', action: 'deny', aggregation: 'and',
    priority: 0, is_active: true,
    conditions: [{ _key: Date.now(), field: 'country', operator: 'equals', value: '' }]
  });

  // Role Mapping Rules state
  const [roleMappingRules, setRoleMappingRules] = useState([]);
  const [roleMappingLoading, setRoleMappingLoading] = useState(false);
  const [roleMappingModalOpen, setRoleMappingModalOpen] = useState(false);
  const [editingRoleMapping, setEditingRoleMapping] = useState(null);
  const EMPTY_RM_FORM = {
    name: '', description: '', priority: 50,
    match_field: 'email_domain', match_operator: 'equals', match_value: '',
    secondary_field: '', secondary_operator: 'equals', secondary_value: '',
    assigned_roles: [], is_active: true, notes: ''
  };
  const [roleMappingForm, setRoleMappingForm] = useState(EMPTY_RM_FORM);

  // Super Admin: SSO Attempts Log state
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const [ssoAttempts, setSsoAttempts] = useState([]);
  const [ssoAttemptsLoading, setSsoAttemptsLoading] = useState(false);
  const [ssoSearchQuery, setSsoSearchQuery] = useState('');
  const [ssoResultFilter, setSsoResultFilter] = useState('');
  
  // Super Admin: Admin Audit Log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditSearchQuery, setAuditSearchQuery] = useState('');

  // Access Rules sub-tab state (for Access Rules / Role Mapping / SSO Log tabs)
  const [accessSubTab, setAccessSubTab] = useState('rules');
  const [testInput, setTestInput] = useState({ country: '', domain: '', domain_type: '', email: '', levelup_role: '' });
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Fetch user statistics
  const fetchUserStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/user-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch user stats');
    }
  };

  // Fetch users with pagination and filters
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        skip: (currentPage * pageSize).toString()
      });
      
      if (userSearch) params.append('search', userSearch);
      if (roleFilter) params.append('role', roleFilter);
      if (ndaFilter) params.append('nda_status', ndaFilter);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotalUsers(data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [currentPage, userSearch, roleFilter, ndaFilter]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setAssets(data.assets || []);
      setTotalAssets(data.total || data.assets?.length || 0);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch feedback
  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: '100' });
      if (feedbackTypeFilter) params.append('type', feedbackTypeFilter);
      if (feedbackStatusFilter && feedbackStatusFilter !== 'hide_resolved') {
        params.append('status', feedbackStatusFilter);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/feedback?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        let filtered = data.feedback || [];
        if (feedbackStatusFilter === 'hide_resolved') {
          filtered = filtered.filter(fb => fb.status !== 'resolved');
        }
        setFeedbackList(filtered);
      }
    } catch (error) {
      toast.error('Failed to load feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Update feedback status
  const handleUpdateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/feedback/${feedbackId}/status`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus })
        }
      );
      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        setFeedbackList(prev => prev.map(fb => fb.id === feedbackId ? { ...fb, status: newStatus } : fb));
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Send weekly report
  const handleSendWeeklyReport = async () => {
    setSendingWeekly(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/weekly/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to send report');
    } finally {
      setSendingWeekly(false);
    }
  };

  // Send monthly content owner reports
  const handleSendMonthlyReports = async () => {
    setSendingMonthly(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/content-owner/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to send reports');
      }
    } catch (error) {
      toast.error('Failed to send reports');
    } finally {
      setSendingMonthly(false);
    }
  };

  const handleSendUploadAccessDigest = async () => {
    setSendingDigest(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/upload-access-digest/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to send digest');
      }
    } catch {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  // === TASK 1: Approval request helpers ===
  const fetchApprovals = async (statusParam, typeParam) => {
    setApprovalsLoading(true);
    const sf = statusParam !== undefined ? statusParam : approvalStatusFilter;
    const rt = typeParam !== undefined ? typeParam : approvalTypeFilter;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (sf) params.append('status_filter', sf);
      if (rt) params.append('request_type', rt);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/approval-requests${qs}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setApprovalRequests(data.requests || []);
      }
    } catch (error) {
      toast.error('Failed to load approval requests');
    } finally {
      setApprovalsLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/approval-requests/${requestId}/approve`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchApprovals();
      } else {
        toast.error(data.detail || 'Failed to approve request');
      }
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId, userEmail, requestType) => {
    const confirmMsg = requestType === 'upload_access'
      ? `Reject upload access request from "${userEmail}"?\n\nThe user will remain on the portal but will not receive upload access.`
      : `Reject this request?\n\nUser "${userEmail}" will be permanently deleted from the portal.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/approval-requests/${requestId}/reject`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchApprovals();
      } else {
        toast.error(data.detail || 'Failed to reject request');
      }
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  // === TASK 2: Access rules helpers ===
  const fetchBlockedCountries = async () => {
    setBlockedCountriesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blocked-countries`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setBlockedCountries(data.blocked_countries || []);
      }
    } catch (error) {
      toast.error('Failed to load blocked countries');
    } finally {
      setBlockedCountriesLoading(false);
    }
  };

  const fetchAccessRules = async () => {
    setAccessRulesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-rules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAccessRules(data.rules || []);
      }
    } catch (error) {
      toast.error('Failed to load access rules');
    } finally {
      setAccessRulesLoading(false);
    }
  };

  const openRuleModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        name: rule.name,
        description: rule.description || '',
        action: rule.action,
        aggregation: rule.aggregation,
        priority: rule.priority,
        is_active: rule.is_active,
        conditions: rule.conditions && rule.conditions.length > 0
          ? rule.conditions.map(c => ({ ...c, _key: c.id }))
          : [{ _key: Date.now(), field: 'country', operator: 'equals', value: '' }]
      });
    } else {
      setEditingRule(null);
      setRuleForm({
        name: '', description: '', action: 'deny', aggregation: 'and',
        priority: 0, is_active: true,
        conditions: [{ _key: Date.now(), field: 'country', operator: 'equals', value: '' }]
      });
    }
    setRuleModalOpen(true);
  };

  const handleSaveRule = async () => {
    if (!ruleForm.name.trim()) { toast.error('Rule name is required'); return; }
    if (ruleForm.conditions.some(c => !c.value.trim())) { toast.error('All condition values must be filled in'); return; }
    try {
      const token = localStorage.getItem('token');
      const url = editingRule
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-rules/${editingRule.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-rules`;
      const method = editingRule ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(ruleForm)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(editingRule ? 'Rule updated' : 'Rule created');
        setRuleModalOpen(false);
        setTestResult(null);
        fetchAccessRules();
      } else {
        toast.error(data.detail || 'Failed to save rule');
      }
    } catch (error) {
      toast.error('Failed to save rule');
    }
  };

  const handleDeleteRule = async (ruleId, ruleName) => {
    if (!window.confirm(`Delete rule "${ruleName}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-rules/${ruleId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success('Rule deleted');
        fetchAccessRules();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete rule');
      }
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleTestAccess = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-rules/test`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(testInput)
        }
      );
      const data = await response.json();
      if (response.ok) {
        setTestResult(data);
      } else {
        toast.error(data.detail || 'Test failed');
      }
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  // ── Role Mapping Rules handlers ─────────────────────────────────────────
  const fetchRoleMappingRules = async () => {
    setRoleMappingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/role-mapping-rules`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRoleMappingRules(data.rules || []);
      }
    } catch { toast.error('Failed to load role mapping rules'); }
    finally { setRoleMappingLoading(false); }
  };

  const openAddRoleMapping = () => {
    setEditingRoleMapping(null);
    setRoleMappingForm(EMPTY_RM_FORM);
    setRoleMappingModalOpen(true);
  };

  const openEditRoleMapping = (rule) => {
    setEditingRoleMapping(rule);
    setRoleMappingForm({
      name: rule.name,
      description: rule.description || '',
      priority: rule.priority,
      match_field: rule.match_field,
      match_operator: rule.match_operator,
      match_value: rule.match_value,
      secondary_field: rule.secondary_field || '',
      secondary_operator: rule.secondary_operator || 'equals',
      secondary_value: rule.secondary_value || '',
      assigned_roles: rule.assigned_roles || [],
      is_active: rule.is_active,
      notes: rule.notes || '',
    });
    setRoleMappingModalOpen(true);
  };

  const handleSaveRoleMapping = async () => {
    if (!roleMappingForm.name.trim()) { toast.error('Rule name is required'); return; }
    if (!roleMappingForm.assigned_roles.length) { toast.error('Select at least one role'); return; }
    const token = localStorage.getItem('token');
    const url = editingRoleMapping
      ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/role-mapping-rules/${editingRoleMapping.id}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/admin/role-mapping-rules`;
    const method = editingRoleMapping ? 'PUT' : 'POST';
    const body = {
      ...roleMappingForm,
      secondary_field: roleMappingForm.secondary_field || null,
      secondary_operator: roleMappingForm.secondary_operator || null,
      secondary_value: roleMappingForm.secondary_value || null,
    };
    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(editingRoleMapping ? 'Rule updated' : 'Rule created');
        setRoleMappingModalOpen(false);
        fetchRoleMappingRules();
      } else { toast.error(data.detail || 'Failed to save rule'); }
    } catch { toast.error('Failed to save rule'); }
  };

  const handleDeleteRoleMapping = async (id, name) => {
    if (!window.confirm(`Delete role mapping rule "${name}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/role-mapping-rules/${id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); fetchRoleMappingRules(); }
      else { toast.error(data.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleRoleMapping = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/role-mapping-rules/${id}/toggle`,
        { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { fetchRoleMappingRules(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed to toggle'); }
    } catch { toast.error('Failed to toggle rule'); }
  };

  const toggleRoleMappingRole = (role) => {
    setRoleMappingForm(prev => ({
      ...prev,
      assigned_roles: prev.assigned_roles.includes(role)
        ? prev.assigned_roles.filter(r => r !== role)
        : [...prev.assigned_roles, role]
    }));
  };

  // ── Super Admin: SSO Attempts Log handlers ─────────────────────────────────────────
  const fetchSsoAttempts = async () => {
    if (!isSuperAdmin) return;
    setSsoAttemptsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (ssoSearchQuery) params.append('search', ssoSearchQuery);
      if (ssoResultFilter) params.append('result_filter', ssoResultFilter);
      params.append('limit', '50');
      
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/sso-attempts?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSsoAttempts(data.attempts || []);
      } else if (res.status === 403) {
        // Not authorized - user is not super_admin
        console.log('SSO Attempts Log requires super_admin role');
      }
    } catch (err) { console.error('Failed to load SSO attempts:', err); }
    finally { setSsoAttemptsLoading(false); }
  };

  // ── Super Admin: Admin Audit Log handlers ─────────────────────────────────────────
  const fetchAuditLogs = async () => {
    if (!isSuperAdmin) return;
    setAuditLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (auditSearchQuery) params.append('search', auditSearchQuery);
      params.append('limit', '100');
      
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-log?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
      }
    } catch (err) { console.error('Failed to load audit logs:', err); }
    finally { setAuditLogsLoading(false); }
  };

  // Pending approval count (for banner) — derived from loaded approvals
  const pendingApprovalCount = approvalRequests.filter(r => r.status === 'pending').length;

  // Fetch stats on mount + initial pending approvals for banner
  useEffect(() => {
    if (isReportViewerOnly) {
      // report_viewer: only show approvals tab
      setActiveTab('approvals');
      setApprovalTypeFilter('upload_access');
      fetchApprovals('pending', 'upload_access');
    } else {
      fetchUserStats();
      fetchApprovals('pending', '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'assets') {
      fetchAssets();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'feedback') {
      fetchFeedback();
    } else if (activeTab === 'approvals') {
      fetchApprovals(approvalStatusFilter, approvalTypeFilter);
    } else if (activeTab === 'access-rules') {
      fetchAccessRules();
      fetchRoleMappingRules();
      fetchBlockedCountries();
      // Fetch SSO attempts if super admin and on the SSO Log sub-tab
      if (isSuperAdmin && accessSubTab === 'sso-log') {
        fetchSsoAttempts();
      }
      if (isSuperAdmin && accessSubTab === 'audit-log') {
        fetchAuditLogs();
      }
    }
  }, [activeTab, fetchUsers, accessSubTab, isSuperAdmin]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'users') {
        setCurrentPage(0);
        fetchUsers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, roleFilter, ndaFilter]);

  // Clear selections when page changes
  useEffect(() => {
    setSelectedUsers(new Set());
  }, [currentPage, userSearch, roleFilter, ndaFilter]);

  // Toggle user selection for bulk delete
  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Select/deselect all users on current page
  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      // Don't allow selecting yourself or other admins
      const selectableUsers = users.filter(u => 
        u.id !== user.id && !u.roles.includes('admin')
      );
      setSelectedUsers(new Set(selectableUsers.map(u => u.id)));
    }
  };

  // Delete single user
  const handleDeleteUser = async (userId) => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`,
        { 
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`User ${data.deleted_email} deleted successfully`);
        setDeleteModalOpen(false);
        setUserToDelete(null);
        fetchUsers();
        fetchUserStats();
      } else {
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Bulk delete users
  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return;
    
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/bulk-delete`,
        { 
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ user_ids: Array.from(selectedUsers) })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`${data.deleted_count} users deleted successfully`);
        setBulkDeleteModalOpen(false);
        setSelectedUsers(new Set());
        fetchUsers();
        fetchUserStats();
      } else {
        toast.error(data.detail || 'Failed to delete users');
      }
    } catch (error) {
      toast.error('Failed to delete users');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Cleanup test accounts
  const handleCleanupTestAccounts = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/cleanup-test-accounts`,
        { 
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`${data.deleted_count} test accounts cleaned up`);
        setCleanupModalOpen(false);
        fetchUsers();
        fetchUserStats();
      } else {
        toast.error(data.detail || 'Failed to cleanup test accounts');
      }
    } catch (error) {
      toast.error('Failed to cleanup test accounts');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Protected accounts that can never be deleted
  const PROTECTED_EMAILS = ['superadmin@technofocus.in'];
  // Only this specific super admin can delete other admin accounts
  const SUPER_ADMIN_EMAIL = 'contact.sg@technofocus.co';
  // Use the role-based isSuperAdmin check (defined earlier) OR email-based fallback
  const isSuperAdminByEmail = (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
  const canDeleteAdmins = isSuperAdmin || isSuperAdminByEmail;

  const canDeleteUser = (u) => {
    if (u.id === user.id) return false;                                         // Never delete yourself
    if (PROTECTED_EMAILS.includes((u.email || '').toLowerCase())) return false; // Protected accounts
    if ((u.roles || []).includes('admin') && !canDeleteAdmins) return false;       // Only super admin can delete other admins
    return true;
  };

  // Fetch database export preview
  const fetchExportPreview = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/export-preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setExportPreview(data);
        setExportModalOpen(true);
      } else {
        toast.error('Failed to fetch export preview');
      }
    } catch (error) {
      toast.error('Failed to fetch export preview');
    } finally {
      setExportLoading(false);
    }
  };

  // Download database export
  const handleDatabaseExport = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/export?include_passwords=${includePasswords}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        // Get the filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'compete_db_export.json';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1];
        }
        
        // Download the file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Database exported successfully');
        setExportModalOpen(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to export database');
      }
    } catch (error) {
      toast.error('Failed to export database');
    } finally {
      setExportLoading(false);
    }
  };

  // Lookup user by LevelUp ID or email domain for role assignment
  const handleLookupUser = async () => {
    if (!lookupEmail.trim()) {
      setLookupError('Please enter a LevelUp ID or email domain');
      return;
    }
    
    const searchTerm = lookupEmail.trim();

    setLookupLoading(true);
    setLookupError('');
    setLookupResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?search=${encodeURIComponent(searchTerm)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        setLookupError('Failed to search user');
        return;
      }

      const data = await response.json();
      
      // Try to find by LevelUp ID first (numeric search)
      let matchedUser = null;
      const isNumeric = /^\d+$/.test(searchTerm);
      
      if (isNumeric) {
        // Search by LevelUp ID
        matchedUser = (data.users || []).find(
          u => u.student_user_id && u.student_user_id.toString() === searchTerm
        );
      }
      
      // If not found by ID, try email/domain match
      if (!matchedUser) {
        matchedUser = (data.users || []).find(
          u => u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
               (u.org_domain && u.org_domain.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      if (matchedUser) {
        setLookupResult(matchedUser);
        setEditingRoles(false);
      } else {
        setLookupError(`No user found with LevelUp ID or domain "${searchTerm}"`);
      }
    } catch (error) {
      setLookupError('Connection error. Please try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleRoleToggle = (role) => {
    if (!lookupResult) return;
    const currentRoles = lookupResult.roles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    setLookupResult({ ...lookupResult, roles: newRoles });
  };

  const handleSaveRoles = async () => {
    if (!lookupResult) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${lookupResult.id}/roles`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(lookupResult.roles)
        }
      );

      if (response.ok) {
        toast.success(`Roles updated for ${lookupResult.email}`);
        setEditingRoles(false);
        fetchUserStats();
        fetchUsers();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to update roles');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }
  };

  const clearLookup = () => {
    setLookupEmail('');
    setLookupResult(null);
    setLookupError('');
    setEditingRoles(false);
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assets/${assetId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Asset deleted');
        fetchAssets();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete asset');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }
  };

  const handleMFAReset = async (targetUserId) => {
    if (!window.confirm('Reset MFA for this admin? They will be prompted to set up MFA again on next login.')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: targetUserId }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'MFA reset successfully');
        setLookupResult(prev => prev ? { ...prev, mfa_enabled: false } : prev);
      } else {
        toast.error(data.detail || 'Failed to reset MFA');
      }
    } catch {
      toast.error('Connection error. Please try again.');
    }
  };

  // Export users function
  const handleExportUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (ndaFilter) params.append('nda_status', ndaFilter);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export-users?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Convert to CSV
        const headers = ['Name', 'Email', 'Organization', 'Country', 'Roles', 'NDA Accepted', 'NDA Accepted At', 'Created At', 'Last Login'];
        const csvRows = [headers.join(',')];
        
        data.data.forEach(user => {
          csvRows.push([
            `"${user.name || ''}"`,
            `"${user.email || ''}"`,
            `"${user.organization || ''}"`,
            `"${user.country || ''}"`,
            `"${user.roles || ''}"`,
            user.nda_accepted,
            `"${user.nda_accepted_at || ''}"`,
            `"${user.created_at || ''}"`,
            `"${user.last_login || ''}"`
          ].join(','));
        });
        
        // Download CSV
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.total} users to CSV`);
      } else {
        toast.error('Failed to export users');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      {/* LevelUp Header */}
      <LevelUpHeader user={user} onLogout={onLogout} />
      <SunsetBanner user={user} />

      <div style={{ flex: 1 }}>
        {/* Navigation */}
        <nav style={{ backgroundColor: '#1B5E5F', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 0' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', display: 'flex', gap: '32px' }}>
            <button onClick={() => navigate('/browse')} style={{ color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <ArrowLeft size={16} /> Back to Why Microsoft – Compete Enablement for Partners
            </button>
          </div>
        </nav>

        {/* Main Content */}
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
          {/* Page Header */}
          <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50', marginBottom: '8px' }}>Admin Dashboard</h1>
              <p style={{ color: '#5A6C7D', fontSize: '14px' }}>Manage users, content, and system settings</p>
            </div>
            {!isReportViewerOnly && (
            <button
              onClick={fetchExportPreview}
              disabled={exportLoading}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6366F1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: exportLoading ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '600',
                fontSize: '14px',
                boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)'
              }}
              data-testid="export-database-btn"
            >
              <Database size={18} />
              {exportLoading ? 'Loading...' : 'Export Database'}
            </button>
            )}
          </div>

          {/* Stats Cards */}
          {userStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <Users size={20} color="#1B5E5F" />
                <span style={{ color: '#5A6C7D', fontSize: '13px' }}>Total Users</span>
              </div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50' }}>{userStats.total_users?.toLocaleString()}</span>
            </div>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <Shield size={20} color="#0C6A1D" />
                <span style={{ color: '#5A6C7D', fontSize: '13px' }}>NDA Accepted</span>
              </div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: '#0C6A1D' }}>{userStats.nda_stats?.accepted?.toLocaleString()}</span>
            </div>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <UserCog size={20} color="#E67E22" />
                <span style={{ color: '#5A6C7D', fontSize: '13px' }}>Admins</span>
              </div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50' }}>{userStats.role_stats?.admin || 0}</span>
            </div>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <FileText size={20} color="#3498DB" />
                <span style={{ color: '#5A6C7D', fontSize: '13px' }}>Content Owners</span>
              </div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50' }}>{userStats.role_stats?.content_owner || 0}</span>
            </div>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <BarChart3 size={20} color="#9B59B6" />
                <span style={{ color: '#5A6C7D', fontSize: '13px' }}>Report Viewers</span>
              </div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50' }}>{userStats.role_stats?.report_viewer || 0}</span>
            </div>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <Building2 size={20} color="#1ABC9C" />
                <span style={{ color: '#5A6C7D', fontSize: '13px' }}>Partners</span>
              </div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50' }}>{userStats.role_stats?.partner || 0}</span>
            </div>
          </div>
        )}

        {/* Pending Approval Banner */}
        {pendingApprovalCount > 0 && activeTab !== 'approvals' && (
          <div
            data-testid="pending-approval-banner"
            style={{
              backgroundColor: '#FEF3C7', borderLeft: '4px solid #F59E0B',
              borderRadius: '6px', padding: '12px 16px', marginBottom: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertTriangle size={18} color="#D97706" />
              <span style={{ color: '#92400E', fontWeight: '600', fontSize: '14px' }}>
                {pendingApprovalCount} pending admin access {pendingApprovalCount === 1 ? 'request' : 'requests'} awaiting review
              </span>
            </div>
            <button
              onClick={() => setActiveTab('approvals')}
              style={{
                background: '#F59E0B', color: 'white', border: 'none',
                borderRadius: '4px', padding: '6px 14px', fontWeight: '600',
                fontSize: '13px', cursor: 'pointer'
              }}
              data-testid="view-approvals-banner-btn"
            >
              Review
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #E0E0E0', paddingBottom: '0', flexWrap: 'wrap' }}>
          {(isReportViewerOnly
            ? ['approvals']
            : ['users', 'role-assignment', 'assets', 'taxonomy', 'feedback', 'reporting', 'approvals', 'access-rules', 'api-docs']
          ).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
              style={{
                padding: '12px 18px',
                border: 'none',
                background: 'none',
                fontSize: '13px',
                fontWeight: '600',
                color: activeTab === tab ? '#1B5E5F' : '#5A6C7D',
                cursor: 'pointer',
                borderBottom: activeTab === tab ? '3px solid #1B5E5F' : '3px solid transparent',
                marginBottom: '-2px',
                position: 'relative',
              }}
            >
              {tab === 'users' && 'User Management'}
              {tab === 'role-assignment' && 'Assign Roles'}
              {tab === 'assets' && 'Content Management'}
              {tab === 'taxonomy' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Settings size={14} />Taxonomy
                </span>
              )}
              {tab === 'feedback' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MessageSquare size={14} />Feedback
                </span>
              )}
              {tab === 'reporting' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Send size={14} />Reporting
                </span>
              )}
              {tab === 'approvals' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={14} />
                  Approvals
                  {pendingApprovalCount > 0 && (
                    <span style={{
                      backgroundColor: '#EF4444', color: 'white', borderRadius: '9999px',
                      padding: '1px 6px', fontSize: '11px', fontWeight: '700', minWidth: '18px', textAlign: 'center'
                    }}>
                      {pendingApprovalCount}
                    </span>
                  )}
                </span>
              )}
              {tab === 'access-rules' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Shield size={14} />Access Rules
                </span>
              )}
              {tab === 'api-docs' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Code2 size={14} />API Docs
                </span>
              )}
            </button>
          ))}
        </div>

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            {/* Filters Row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                  <input
                    type="text"
                    placeholder="Search by name, email, or organization..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 40px',
                      border: '1px solid #E0E0E0',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    data-testid="user-search-input"
                  />
                </div>
              </div>
              
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(0); }}
                style={{ padding: '10px 12px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '14px', minWidth: '150px' }}
                data-testid="role-filter"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="content_owner">Content Owner</option>
                <option value="report_viewer">Report Viewer</option>
                <option value="partner">Partner</option>
              </select>
              
              <select
                value={ndaFilter}
                onChange={(e) => { setNdaFilter(e.target.value); setCurrentPage(0); }}
                style={{ padding: '10px 12px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '14px', minWidth: '150px' }}
                data-testid="nda-filter"
              >
                <option value="">All NDA Status</option>
                <option value="accepted">NDA Accepted</option>
                <option value="pending">NDA Pending</option>
              </select>
              
              <button
                onClick={() => fetchUsers()}
                style={{ padding: '10px 16px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <RefreshCw size={16} /> Refresh
              </button>
              
              <button
                onClick={handleExportUsers}
                style={{ padding: '10px 16px', backgroundColor: '#1B5E5F', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                data-testid="export-users-btn"
              >
                <Download size={16} /> Export CSV
              </button>
              
              {/* Delete Actions */}
              {selectedUsers.size > 0 && (
                <button
                  onClick={() => setBulkDeleteModalOpen(true)}
                  style={{ padding: '10px 16px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 size={16} /> Delete Selected ({selectedUsers.size})
                </button>
              )}
              
              <button
                onClick={() => setCleanupModalOpen(true)}
                style={{ padding: '10px 16px', backgroundColor: '#F59E0B', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                data-testid="cleanup-test-btn"
              >
                <AlertTriangle size={16} /> Cleanup Test Accounts
              </button>
            </div>

            {/* Results info */}
            <div style={{ marginBottom: '16px', fontSize: '14px', color: '#5A6C7D', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                Showing {users.length} of {totalUsers.toLocaleString()} users
                {(roleFilter || ndaFilter || userSearch) && ' (filtered)'}
              </span>
              {selectedUsers.size > 0 && (
                <span style={{ color: '#DC2626', fontWeight: '500' }}>
                  {selectedUsers.size} user(s) selected for deletion
                </span>
              )}
            </div>

            {/* User Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F9FAFB' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.size > 0 && selectedUsers.size === users.filter(u => canDeleteUser(u)).length}
                        onChange={toggleSelectAll}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        title="Select all deletable users"
                      />
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>User</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>LevelUp ID</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Email Domain</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Roles</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>NDA Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Created</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase', width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
                  ) : users.length === 0 ? (
                    <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>No users found</td></tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id} style={{ borderTop: '1px solid #E5E7EB', backgroundColor: selectedUsers.has(u.id) ? '#FEF2F2' : 'transparent' }}>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {canDeleteUser(u) ? (
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(u.id)}
                              onChange={() => toggleUserSelection(u.id)}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                          ) : (
                            <span title={u.id === user.id ? "You" : "Admin user"} style={{ color: '#9CA3AF' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{u.name}</span>
                            {(u.is_sso_user === false || (u.roles || []).some(r => ['admin', 'content_owner', 'report_viewer'].includes(r))) && (
                              <span style={{ fontSize: '12px', color: '#5A6C7D' }}>{u.email}</span>
                            )}
                            {u.id === user.id && <span style={{ fontSize: '11px', color: '#1B5E5F', fontWeight: '600' }}>(You)</span>}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>
                          {u.student_user_id ? (
                            <span style={{ fontFamily: 'monospace', backgroundColor: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>
                              #{u.student_user_id}
                            </span>
                          ) : (
                            <span style={{ color: '#9CA3AF' }}>Local User</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{u.org_domain || '-'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {(u.roles || []).map(role => (
                              <span key={role} style={{
                                backgroundColor: role === 'admin' ? '#FEF3C7' : role === 'content_owner' ? '#DBEAFE' : role === 'report_viewer' ? '#F3E8FF' : '#D1FAE5',
                                color: role === 'admin' ? '#92400E' : role === 'content_owner' ? '#1E40AF' : role === 'report_viewer' ? '#6B21A8' : '#065F46',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase'
                              }}>
                                {role.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {u.nda_accepted ? (
                            <span style={{ color: '#0C6A1D', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <CheckCircle size={14} /> Accepted
                            </span>
                          ) : (
                            <span style={{ color: '#DC2626', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <AlertCircle size={14} /> Pending
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#9CA3AF' }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {canDeleteUser(u) ? (
                            <button
                              onClick={() => { setUserToDelete(u); setDeleteModalOpen(true); }}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#FEE2E2',
                                color: '#DC2626',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px'
                              }}
                              title="Delete user"
                            >
                              <Trash2 size={14} />
                            </button>
                          ) : (
                            <span style={{ color: '#9CA3AF', fontSize: '12px' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '14px', color: '#5A6C7D' }}>
                  Page {currentPage + 1} of {totalPages}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #E0E0E0',
                      borderRadius: '6px',
                      backgroundColor: currentPage === 0 ? '#F9FAFB' : 'white',
                      cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #E0E0E0',
                      borderRadius: '6px',
                      backgroundColor: currentPage >= totalPages - 1 ? '#F9FAFB' : 'white',
                      cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Role Assignment Tab */}
        {activeTab === 'role-assignment' && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>
              Assign User Roles
            </h3>
            <p style={{ color: '#5A6C7D', fontSize: '14px', marginBottom: '24px' }}>
              Search for a user by their LevelUp ID (e.g., 9152606) or email domain to view and modify their roles.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input
                  type="text"
                  placeholder="Enter LevelUp ID or email domain"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookupUser()}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 42px',
                    border: '2px solid #E0E0E0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  data-testid="role-lookup-input"
                />
              </div>
              <button
                onClick={handleLookupUser}
                disabled={lookupLoading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1B5E5F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: lookupLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                data-testid="role-lookup-btn"
              >
                <Search size={18} />
                {lookupLoading ? 'Searching...' : 'Lookup User'}
              </button>
              {lookupResult && (
                <button
                  onClick={clearLookup}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#F3F4F6',
                    color: '#5A6C7D',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            
            {lookupError && (
              <div style={{ padding: '12px 16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', color: '#DC2626', fontSize: '14px', marginBottom: '16px' }}>
                {lookupError}
              </div>
            )}
            
            {lookupResult && (
              <div style={{ padding: '24px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Name</span>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginTop: '4px' }}>{lookupResult.name}</p>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Email</span>
                    <p style={{ fontSize: '16px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>{lookupResult.email}</p>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Organization</span>
                    <p style={{ fontSize: '16px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>{lookupResult.org_domain || '-'}</p>
                  </div>
                </div>
                
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#2C3E50' }}>User Roles</span>
                    {!editingRoles && (
                      <button
                        onClick={() => setEditingRoles(true)}
                        style={{ padding: '6px 12px', backgroundColor: '#E0F2F1', color: '#1B5E5F', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Edit Roles
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {['admin', 'content_owner', 'report_viewer', 'partner'].map(role => {
                      // Check if role can be assigned based on user's domain
                      const userDomain = lookupResult.org_domain?.toLowerCase() || '';
                      const isSsoUser = lookupResult.student_user_id !== null;
                      
                      let roleDisabled = false;
                      let roleTooltip = '';
                      
                      if (role === 'admin') {
                        const canAssign = isSsoUser && (userDomain === 'technofocus.in' || userDomain === 'technofocus.co');
                        roleDisabled = !canAssign;
                        roleTooltip = 'Admin: Only Technofocus SSO users (technofocus.in/co)';
                      } else if (role === 'content_owner') {
                        const canAssign = isSsoUser && userDomain === 'microsoft.com';
                        roleDisabled = !canAssign;
                        roleTooltip = 'Content Owner: Only Microsoft SSO users (microsoft.com)';
                      } else if (role === 'report_viewer') {
                        const canAssign = isSsoUser && (userDomain === 'microsoft.com' || userDomain === 'technofocus.in' || userDomain === 'technofocus.co');
                        roleDisabled = !canAssign;
                        roleTooltip = 'Report Viewer: Microsoft or Technofocus SSO users';
                      }
                      
                      return (
                        <label
                          key={role}
                          title={roleTooltip}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 16px',
                            backgroundColor: (lookupResult.roles || []).includes(role) ? '#E0F2F1' : 'white',
                            border: `2px solid ${(lookupResult.roles || []).includes(role) ? '#1B5E5F' : roleDisabled ? '#FCA5A5' : '#E0E0E0'}`,
                            borderRadius: '8px',
                            cursor: editingRoles && !roleDisabled ? 'pointer' : roleDisabled ? 'not-allowed' : 'default',
                            opacity: roleDisabled ? 0.5 : (editingRoles ? 1 : 0.7)
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={(lookupResult.roles || []).includes(role)}
                            onChange={() => editingRoles && !roleDisabled && handleRoleToggle(role)}
                            disabled={!editingRoles || roleDisabled}
                            style={{ width: '16px', height: '16px', accentColor: '#1B5E5F' }}
                          />
                          <span style={{ fontWeight: '500', color: roleDisabled ? '#9CA3AF' : '#2C3E50', textTransform: 'capitalize' }}>
                            {role.replace('_', ' ')}
                          </span>
                          {roleDisabled && <span style={{ fontSize: '10px', color: '#DC2626' }}>✕</span>}
                        </label>
                      );
                    })}
                  </div>
                  
                  {/* Role assignment rules notice */}
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '6px', fontSize: '12px', color: '#92400E' }}>
                    <strong>Role Assignment Rules:</strong>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      <li><strong>Admin:</strong> Only SSO users from technofocus.in or technofocus.co</li>
                      <li><strong>Content Owner:</strong> Only SSO users from microsoft.com</li>
                      <li><strong>Report Viewer:</strong> SSO users from microsoft.com, technofocus.in, or technofocus.co</li>
                      <li><strong>Partner:</strong> Any user</li>
                    </ul>
                  </div>
                  
                  {editingRoles && (
                    <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                      <button
                        onClick={handleSaveRoles}
                        style={{ padding: '10px 24px', backgroundColor: '#0C6A1D', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => { setEditingRoles(false); handleLookupUser(); }}
                        style={{ padding: '10px 24px', backgroundColor: '#F3F4F6', color: '#5A6C7D', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {/* MFA Reset section (for admin users) */}
                  {(lookupResult.roles || []).includes('admin') && (
                    <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#991B1B', marginBottom: '8px' }}>MFA Management</p>
                      <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                        Reset MFA for this admin if they've lost access to their authenticator app. They'll be prompted to set up MFA again on next login.
                      </p>
                      <p style={{ fontSize: '12px', color: '#374151', marginBottom: '12px' }}>
                        <strong>MFA Status:</strong>{' '}
                        <span style={{ color: lookupResult.mfa_enabled ? '#16A34A' : '#DC2626', fontWeight: '600' }}>
                          {lookupResult.mfa_enabled ? 'Enabled' : 'Not set up'}
                        </span>
                      </p>
                      {lookupResult.mfa_enabled && (
                        <button
                          onClick={() => handleMFAReset(lookupResult.id)}
                          data-testid="mfa-reset-btn"
                          style={{ padding: '8px 16px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Reset MFA for this user
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assets Tab */}
        {activeTab === 'assets' && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50' }}>Content Management</h3>
              <span style={{ color: '#5A6C7D', fontSize: '14px' }}>{assets.length} assets</span>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F9FAFB' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Title</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Type</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Solution Area</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Downloads</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
                  ) : assets.length === 0 ? (
                    <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>No assets found</td></tr>
                  ) : (
                    assets.map(asset => (
                      <tr key={asset.id} style={{ borderTop: '1px solid #E5E7EB' }}>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>{asset.title}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D', textTransform: 'capitalize' }}>{asset.content_type?.replace('_', ' ')}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{asset.solution_area}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{asset.download_count || 0}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button
                            onClick={() => handleDeleteAsset(asset.id)}
                            style={{ padding: '6px 12px', backgroundColor: '#FEF2F2', color: '#DC2626', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Delete Single User Modal */}
      {deleteModalOpen && userToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#FEE2E2', borderRadius: '50%', padding: '10px' }}>
                <Trash2 size={24} color="#DC2626" />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>Delete User</h3>
            </div>
            <p style={{ color: '#5A6C7D', marginBottom: '8px' }}>
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div style={{ backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontWeight: '500', color: '#2C3E50' }}>{userToDelete.name}</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#5A6C7D' }}>{userToDelete.email}</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteModalOpen(false); setUserToDelete(null); }}
                style={{ padding: '10px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(userToDelete.id)}
                disabled={deleteLoading}
                style={{ padding: '10px 20px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', cursor: deleteLoading ? 'wait' : 'pointer', fontWeight: '600' }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {bulkDeleteModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#FEE2E2', borderRadius: '50%', padding: '10px' }}>
                <Trash2 size={24} color="#DC2626" />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>Delete {selectedUsers.size} Users</h3>
            </div>
            <p style={{ color: '#5A6C7D', marginBottom: '16px' }}>
              Are you sure you want to delete <strong>{selectedUsers.size}</strong> selected users? This action cannot be undone.
            </p>
            <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <p style={{ margin: 0, color: '#DC2626', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={16} />
                Warning: All selected user accounts and their data will be permanently deleted.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setBulkDeleteModalOpen(false)}
                style={{ padding: '10px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={deleteLoading}
                style={{ padding: '10px 20px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', cursor: deleteLoading ? 'wait' : 'pointer', fontWeight: '600' }}
              >
                {deleteLoading ? 'Deleting...' : `Delete ${selectedUsers.size} Users`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Test Accounts Modal */}
      {cleanupModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '550px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#FEF3C7', borderRadius: '50%', padding: '10px' }}>
                <AlertTriangle size={24} color="#F59E0B" />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>Cleanup Test Accounts</h3>
            </div>
            <p style={{ color: '#5A6C7D', marginBottom: '16px' }}>
              This will automatically delete all accounts that match test patterns:
            </p>
            <div style={{ backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#5A6C7D', fontSize: '14px', lineHeight: '1.8' }}>
                <li>Emails with timestamps (e.g., user_20251229_115527@...)</li>
                <li>Emails starting with test_, partner_, admin_, owner_, no_nda_</li>
                <li>Emails from test domains: testcompany.com, testpartner.com, betacompany.com</li>
              </ul>
            </div>
            <div style={{ backgroundColor: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <p style={{ margin: 0, color: '#1E40AF', fontSize: '14px' }}>
                <strong>Protected:</strong> Admin accounts and your own account will never be deleted.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCleanupModalOpen(false)}
                style={{ padding: '10px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCleanupTestAccounts}
                disabled={deleteLoading}
                style={{ padding: '10px 20px', backgroundColor: '#F59E0B', color: 'white', border: 'none', borderRadius: '6px', cursor: deleteLoading ? 'wait' : 'pointer', fontWeight: '600' }}
              >
                {deleteLoading ? 'Cleaning up...' : 'Cleanup Test Accounts'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Database Export Modal */}
      {exportModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '550px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#E0E7FF', borderRadius: '50%', padding: '10px' }}>
                <Database size={24} color="#6366F1" />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>Export Database</h3>
            </div>
            <p style={{ color: '#5A6C7D', marginBottom: '16px' }}>
              Export the entire database as a JSON file for backup or migration purposes.
            </p>
            
            {/* Export Preview */}
            {exportPreview && (
              <div style={{ backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#2C3E50' }}>
                  <HardDrive size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Database: {exportPreview.database_name}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                  {Object.entries(exportPreview.collections || {}).map(([name, count]) => (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                      <span style={{ fontSize: '13px', color: '#5A6C7D', textTransform: 'capitalize' }}>{name.replace('_', ' ')}</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#2C3E50' }}>{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: '#E0E7FF', borderRadius: '6px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#4338CA' }}>Total Documents</span>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#4338CA' }}>{exportPreview.total_documents?.toLocaleString()}</span>
                </div>
              </div>
            )}
            
            {/* Options */}
            <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includePasswords}
                  onChange={(e) => setIncludePasswords(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#92400E' }}>Include password hashes</span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#B45309' }}>
                    Warning: Only enable if sharing with trusted team for full database migration
                  </p>
                </div>
              </label>
            </div>
            
            <div style={{ backgroundColor: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <p style={{ margin: 0, color: '#1E40AF', fontSize: '13px' }}>
                <strong>Note:</strong> The export will include all collections: users, assets, NDAs, downloads, and event logs. 
                {!includePasswords && ' Password hashes will be redacted for security.'}
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setExportModalOpen(false); setIncludePasswords(false); }}
                style={{ padding: '10px 20px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDatabaseExport}
                disabled={exportLoading}
                style={{ padding: '10px 20px', backgroundColor: '#6366F1', color: 'white', border: 'none', borderRadius: '6px', cursor: exportLoading ? 'wait' : 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={16} />
                {exportLoading ? 'Exporting...' : 'Download JSON Export'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Taxonomy Management Tab */}
      {activeTab === 'taxonomy' && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <TaxonomyManager />
        </div>
      )}

      {/* Feedback Report Tab */}
      {activeTab === 'feedback' && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '4px' }}>Feedback Report</h3>
              <p style={{ color: '#5A6C7D', fontSize: '14px', margin: 0 }}>Review all user feedback submitted via the portal widget</p>
            </div>
            <button
              onClick={fetchFeedback}
              style={{ padding: '10px 16px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <select
              value={feedbackTypeFilter}
              onChange={(e) => { setFeedbackTypeFilter(e.target.value); }}
              style={{ padding: '10px 12px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '14px', minWidth: '160px' }}
              data-testid="feedback-type-filter"
            >
              <option value="">All Types</option>
              <option value="general">General Feedback</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="content">Content Request</option>
              <option value="usability">Usability Issue</option>
              <option value="performance">Performance Issue</option>
              <option value="design">Design Suggestion</option>
              <option value="documentation">Documentation</option>
              <option value="other">Other</option>
            </select>
            <select
              value={feedbackStatusFilter}
              onChange={(e) => { setFeedbackStatusFilter(e.target.value); }}
              style={{ padding: '10px 12px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '14px', minWidth: '140px' }}
              data-testid="feedback-status-filter"
            >
              <option value="hide_resolved">Hide Resolved</option>
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
            </select>
            <button
              onClick={fetchFeedback}
              style={{ padding: '10px 20px', backgroundColor: '#1B5E5F', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
              data-testid="feedback-apply-filter-btn"
            >
              Apply
            </button>
            <button
              onClick={() => {
                const token = localStorage.getItem('token');
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/feedback/export?token=${token}`, '_blank');
              }}
              style={{ padding: '10px 20px', backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #E0E0E0', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
              data-testid="feedback-export-csv-btn"
            >
              <Download size={16} /> Export CSV
            </button>
          </div>

          {/* Summary Cards */}
          {feedbackList.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {(() => {
                const typeCounts = {};
                feedbackList.forEach(f => { typeCounts[f.type] = (typeCounts[f.type] || 0) + 1; });
                const typeColors = {
                  bug: { bg: '#FEF2F2', color: '#DC2626' },
                  usability: { bg: '#FEF3C7', color: '#92400E' },
                  feature: { bg: '#DBEAFE', color: '#1E40AF' },
                  content: { bg: '#D1FAE5', color: '#065F46' },
                  general: { bg: '#F3F4F6', color: '#374151' },
                  performance: { bg: '#FDE68A', color: '#78350F' },
                  design: { bg: '#F3E8FF', color: '#6B21A8' },
                  documentation: { bg: '#E0F2FE', color: '#0369A1' },
                  other: { bg: '#F1F5F9', color: '#475569' },
                };
                return Object.entries(typeCounts).map(([type, count]) => (
                  <div key={type} style={{ backgroundColor: typeColors[type]?.bg || '#F3F4F6', borderRadius: '8px', padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: typeColors[type]?.color || '#374151' }}>{count}</span>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', fontWeight: '600', color: typeColors[type]?.color || '#5A6C7D', textTransform: 'capitalize' }}>{type.replace('_', ' ')}</p>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* Feedback Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Rating</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase', maxWidth: '400px' }}>Message</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>User</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {feedbackLoading ? (
                  <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
                ) : feedbackList.length === 0 ? (
                  <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>No feedback found</td></tr>
                ) : (
                  feedbackList.map((fb) => {
                    const typeColors = {
                      bug: { bg: '#FEF2F2', color: '#DC2626' },
                      usability: { bg: '#FEF3C7', color: '#92400E' },
                      feature: { bg: '#DBEAFE', color: '#1E40AF' },
                      content: { bg: '#D1FAE5', color: '#065F46' },
                      general: { bg: '#F3F4F6', color: '#374151' },
                    };
                    const tc = typeColors[fb.type] || { bg: '#F3F4F6', color: '#374151' };
                    return (
                      <tr key={fb.id} style={{ borderTop: '1px solid #E5E7EB' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            backgroundColor: tc.bg,
                            color: tc.color,
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'capitalize',
                            whiteSpace: 'nowrap'
                          }}>
                            {fb.type?.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {fb.rating ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                              {[1, 2, 3, 4, 5].map(s => (
                                <Star key={s} size={14} fill={s <= fb.rating ? '#F59E0B' : 'none'} color={s <= fb.rating ? '#F59E0B' : '#D1D5DB'} />
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#9CA3AF', fontSize: '13px' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151', maxWidth: '400px' }}>
                          <p style={{ margin: 0, lineHeight: '1.5', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                            {fb.message}
                          </p>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#5A6C7D' }}>
                          <div>{fb.user_name || '-'}</div>
                          <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{fb.user_email}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {fb.created_at ? new Date(fb.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <select
                            value={fb.status || 'new'}
                            onChange={(e) => handleUpdateFeedbackStatus(fb.id, e.target.value)}
                            data-testid={`feedback-status-${fb.id}`}
                            style={{
                              backgroundColor: fb.status === 'resolved' ? '#D1FAE5' : fb.status === 'reviewed' ? '#FEF3C7' : '#DBEAFE',
                              color: fb.status === 'resolved' ? '#065F46' : fb.status === 'reviewed' ? '#92400E' : '#1E40AF',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              border: '1px solid transparent',
                              cursor: 'pointer',
                              appearance: 'auto',
                            }}
                          >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="resolved">Resolved</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reporting Tab */}
      {activeTab === 'reporting' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Send Report Actions */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Send Reports</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1B5E5F', marginBottom: '8px' }}>Weekly Platform Report</h4>
                <p style={{ fontSize: '13px', color: '#5A6C7D', marginBottom: '4px' }}>Downloads, new users, top assets, assets by solution area, feature requests.</p>
                <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '16px' }}>Auto-sends every Monday 8:00 AM UTC to all Report Viewers and Admins.</p>
                <button
                  onClick={handleSendWeeklyReport}
                  disabled={sendingWeekly}
                  data-testid="send-weekly-report-btn"
                  style={{ padding: '10px 20px', backgroundColor: '#1B5E5F', color: 'white', border: 'none', borderRadius: '6px', cursor: sendingWeekly ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', opacity: sendingWeekly ? 0.6 : 1 }}
                >
                  <Send size={16} /> {sendingWeekly ? 'Sending...' : 'Send Now'}
                </button>
              </div>
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1B5E5F', marginBottom: '8px' }}>Monthly Content Owner Report</h4>
                <p style={{ fontSize: '13px', color: '#5A6C7D', marginBottom: '4px' }}>Individual performance reports sent to each content owner. No PII included.</p>
                <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '16px' }}>Manual trigger only. Sends to all users with content_owner role.</p>
                <button
                  onClick={handleSendMonthlyReports}
                  disabled={sendingMonthly}
                  data-testid="send-monthly-report-btn"
                  style={{ padding: '10px 20px', backgroundColor: '#0C6A1D', color: 'white', border: 'none', borderRadius: '6px', cursor: sendingMonthly ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', opacity: sendingMonthly ? 0.6 : 1 }}
                >
                  <Send size={16} /> {sendingMonthly ? 'Sending...' : 'Send Content Owner Reports'}
                </button>
              </div>
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', borderLeft: '3px solid #0F4C75' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0F4C75', marginBottom: '8px' }}>Upload Access Pending Digest</h4>
                <p style={{ fontSize: '13px', color: '#5A6C7D', marginBottom: '4px' }}>Summary of pending upload access requests awaiting review by report viewers and admins.</p>
                <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '16px' }}>Auto-sends every Wednesday 8:00 AM UTC. Skipped automatically if no pending requests.</p>
                <button
                  onClick={handleSendUploadAccessDigest}
                  disabled={sendingDigest}
                  data-testid="send-upload-access-digest-btn"
                  style={{ padding: '10px 20px', backgroundColor: '#0F4C75', color: 'white', border: 'none', borderRadius: '6px', cursor: sendingDigest ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', opacity: sendingDigest ? 0.6 : 1 }}
                >
                  <Send size={16} /> {sendingDigest ? 'Sending...' : 'Send Digest Now'}
                </button>
              </div>
            </div>
          </div>

          {/* Recipients Info */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '12px' }}>Report Recipients</h3>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '220px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Mail size={16} color="#1B5E5F" />
                  <span style={{ fontWeight: '600', color: '#2C3E50', fontSize: '14px' }}>Weekly Report</span>
                </div>
                <p style={{ fontSize: '13px', color: '#5A6C7D', margin: 0 }}>
                  Sent automatically to all users with <strong>Report Viewer</strong> or <strong>Admin</strong> roles. To manage recipients, assign or remove these roles in the "Assign Roles" tab.
                </p>
              </div>
              <div style={{ flex: 1, minWidth: '220px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Mail size={16} color="#0C6A1D" />
                  <span style={{ fontWeight: '600', color: '#2C3E50', fontSize: '14px' }}>Monthly Content Report</span>
                </div>
                <p style={{ fontSize: '13px', color: '#5A6C7D', margin: 0 }}>
                  Sent to each user with <strong>Content Owner</strong> role, containing performance stats for their uploaded content. No PII included.
                </p>
              </div>
              <div style={{ flex: 1, minWidth: '220px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', borderLeft: '3px solid #0F4C75' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Mail size={16} color="#0F4C75" />
                  <span style={{ fontWeight: '600', color: '#2C3E50', fontSize: '14px' }}>Upload Access Digest</span>
                </div>
                <p style={{ fontSize: '13px', color: '#5A6C7D', margin: 0 }}>
                  Sent every Wednesday to all <strong>Report Viewers</strong> and <strong>Admins</strong> with a list of pending Content Owner access requests. Only sent when there are pending requests.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== APPROVALS TAB (Task 1 + Upload Access) ===== */}
      {activeTab === 'approvals' && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2C3E50', margin: '0 0 4px 0' }}>
                {(approvalTypeFilter === 'upload_access' || isReportViewerOnly)
                  ? 'Content Owner Access Approvals'
                  : approvalTypeFilter === 'admin_access'
                    ? 'Admin Access Approvals'
                    : 'Approvals'}
              </h3>
              {(approvalTypeFilter === 'upload_access' || isReportViewerOnly) && (
                <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', maxWidth: '480px', lineHeight: 1.4 }}>
                  Approve or reject requests from Microsoft users to allow them access to upload Content to this portal as content owners.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
              {/* Request type sub-filter (admin only) */}
              {!isReportViewerOnly && (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: '500' }}>Type:</span>
                  {[
                    { value: '', label: 'All' },
                    { value: 'admin_access', label: 'Admin Requests' },
                    { value: 'upload_access', label: 'Upload Access' },
                  ].map(({ value, label }) => (
                    <button
                      key={value || 'all-type'}
                      onClick={() => { setApprovalTypeFilter(value); fetchApprovals(approvalStatusFilter, value); }}
                      data-testid={`approval-type-filter-${value || 'all'}`}
                      style={{
                        padding: '4px 12px', borderRadius: '9999px', border: '1px solid',
                        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                        backgroundColor: approvalTypeFilter === value ? '#0F4C75' : 'white',
                        color: approvalTypeFilter === value ? 'white' : '#5A6C7D',
                        borderColor: approvalTypeFilter === value ? '#0F4C75' : '#D1D5DB',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {/* Status filter */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {['pending', 'approved', 'rejected', ''].map(s => (
                  <button
                    key={s || 'all'}
                    onClick={() => { setApprovalStatusFilter(s); fetchApprovals(s, approvalTypeFilter); }}
                    data-testid={`approval-filter-${s || 'all'}`}
                    style={{
                      padding: '6px 14px', borderRadius: '9999px', border: '1px solid',
                      fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      backgroundColor: approvalStatusFilter === s ? '#1B5E5F' : 'white',
                      color: approvalStatusFilter === s ? 'white' : '#5A6C7D',
                      borderColor: approvalStatusFilter === s ? '#1B5E5F' : '#D1D5DB',
                    }}
                  >
                    {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
                <button
                  onClick={() => fetchApprovals(approvalStatusFilter, approvalTypeFilter)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B5E5F' }}
                  title="Refresh"
                  data-testid="refresh-approvals-btn"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>
          </div>

          {approvalsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading...</div>
          ) : approvalRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
              <CheckCircle size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
              <p style={{ margin: 0 }}>No {approvalStatusFilter || ''} approval requests</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                    {(approvalTypeFilter === 'upload_access' || isReportViewerOnly
                      ? ['User', 'Email', 'Team / Role', 'Reason', 'Requested', 'Status', 'Actions']
                      : approvalTypeFilter === 'admin_access'
                        ? ['User', 'Email', 'LevelUp ID', 'Requested', 'Status', 'Actions']
                        : ['User', 'Email', 'Type', 'Detail', 'Requested', 'Status', 'Actions']
                    ).map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {approvalRequests.map(req => {
                    const isUpload = req.request_type === 'upload_access';
                    const showMixed = approvalTypeFilter === '' && !isReportViewerOnly;
                    return (
                      <tr key={req.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`approval-row-${req.id}`}>
                        <td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{req.user_name}</td>
                        <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px' }}>{req.user_email}</td>

                        {/* Mixed view: show type badge */}
                        {showMixed && (
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600',
                              backgroundColor: isUpload ? '#DBEAFE' : '#FEF3C7',
                              color: isUpload ? '#1E40AF' : '#92400E',
                            }}>
                              {isUpload ? 'Upload Access' : 'Admin Access'}
                            </span>
                          </td>
                        )}

                        {/* Detail column */}
                        {showMixed && (
                          <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px', maxWidth: '200px' }}>
                            {isUpload
                              ? <span title={req.user_reason || ''}>{req.user_team ? `${req.user_team} — ` : ''}{(req.user_reason || '').slice(0, 60)}{(req.user_reason || '').length > 60 ? '…' : ''}</span>
                              : req.levelup_id || '—'
                            }
                          </td>
                        )}

                        {/* Upload-only view columns */}
                        {(approvalTypeFilter === 'upload_access' || isReportViewerOnly) && (
                          <>
                            <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px' }}>{req.user_team || '—'}</td>
                            <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px', maxWidth: '220px' }}>
                              <span title={req.user_reason || ''}>
                                {(req.user_reason || '').slice(0, 80)}{(req.user_reason || '').length > 80 ? '…' : ''}
                              </span>
                            </td>
                          </>
                        )}

                        {/* Admin-only view column */}
                        {approvalTypeFilter === 'admin_access' && (
                          <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px' }}>{req.levelup_id || '—'}</td>
                        )}

                        <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px' }}>
                          {req.requested_at ? new Date(req.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600',
                            backgroundColor: req.status === 'pending' ? '#FEF3C7' : req.status === 'approved' ? '#D1FAE5' : '#FEE2E2',
                            color: req.status === 'pending' ? '#92400E' : req.status === 'approved' ? '#065F46' : '#991B1B',
                          }}>
                            {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {req.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleApproveRequest(req.id)}
                                data-testid={`approve-btn-${req.id}`}
                                style={{
                                  padding: '6px 14px', backgroundColor: '#D1FAE5', color: '#065F46',
                                  border: '1px solid #A7F3D0', borderRadius: '6px', cursor: 'pointer',
                                  fontWeight: '600', fontSize: '13px'
                                }}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectRequest(req.id, req.user_email, req.request_type)}
                                data-testid={`reject-btn-${req.id}`}
                                style={{
                                  padding: '6px 14px', backgroundColor: '#FEE2E2', color: '#991B1B',
                                  border: '1px solid #FECACA', borderRadius: '6px', cursor: 'pointer',
                                  fontWeight: '600', fontSize: '13px'
                                }}
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          {req.status !== 'pending' && (
                            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                              {req.reviewed_at ? new Date(req.reviewed_at).toLocaleDateString('en-GB') : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '6px', fontSize: '13px', color: '#5A6C7D' }}>
            {isReportViewerOnly ? (
              <><strong>Upload Access Requests:</strong> Microsoft.com and Technofocus users can request content upload access from a floating widget on the portal.
              Approving grants them the <strong>Content Owner</strong> role. Rejecting notifies them by email but keeps their account intact.</>
            ) : (
              <><strong>How it works:</strong> <em>Admin Access</em> — LevelUp Administrators from non-Technofocus domains are assigned Partner role pending approval here.
              Approving grants Admin access; Rejecting deletes them from the portal.
              <em style={{ marginLeft: '12px' }}>Upload Access</em> — Microsoft/Technofocus users can request Content Owner role via the portal widget.
              Approving grants them Content Owner role; Rejecting keeps their account but denies upload access.</>
            )}
          </div>
        </div>
      )}

      {/* ===== ACCESS RULES TAB (Task 2) ===== */}
      {activeTab === 'access-rules' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Sub-tabs for Access Rules / Role Mapping / SSO Attempts Log */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
              <button
                onClick={() => setAccessSubTab('rules')}
                data-testid="access-subtab-rules"
                style={{
                  padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  backgroundColor: accessSubTab === 'rules' ? '#1B5E5F' : '#F3F4F6',
                  color: accessSubTab === 'rules' ? 'white' : '#6B7280',
                  border: 'none',
                }}
              >
                <Shield size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Access Rules
                <span style={{ marginLeft: '6px', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', backgroundColor: accessSubTab === 'rules' ? 'rgba(255,255,255,0.2)' : '#E5E7EB' }}>
                  {accessRules.length}
                </span>
              </button>
              <button
                onClick={() => setAccessSubTab('role-mapping')}
                data-testid="access-subtab-role-mapping"
                style={{
                  padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  backgroundColor: accessSubTab === 'role-mapping' ? '#1B5E5F' : '#F3F4F6',
                  color: accessSubTab === 'role-mapping' ? 'white' : '#6B7280',
                  border: 'none',
                }}
              >
                <Users size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Role Mapping
                <span style={{ marginLeft: '6px', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', backgroundColor: accessSubTab === 'role-mapping' ? 'rgba(255,255,255,0.2)' : '#E5E7EB' }}>
                  {roleMappingRules.length}
                </span>
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => { setAccessSubTab('sso-log'); fetchSsoAttempts(); }}
                  data-testid="access-subtab-sso-log"
                  style={{
                    padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                    backgroundColor: accessSubTab === 'sso-log' ? '#1B5E5F' : '#F3F4F6',
                    color: accessSubTab === 'sso-log' ? 'white' : '#6B7280',
                    border: 'none',
                  }}
                >
                  <AlertCircle size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  SSO Attempts Log
                </button>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px', marginBottom: 0 }}>
              {accessSubTab === 'rules' && 'Control who can access the portal and manage access rules based on LevelUp SSO attributes.'}
              {accessSubTab === 'role-mapping' && 'Configure how portal roles are assigned based on LevelUp SSO attributes.'}
              {accessSubTab === 'sso-log' && 'View recent SSO login attempts with LevelUp token data for debugging access issues.'}
            </p>
          </div>

          {/* ── Access Rules Sub-tab ── */}
          {accessSubTab === 'rules' && (
            <>
          {/* Rules List */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2C3E50', margin: 0 }}>Access Rule Engine</h3>
                <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '4px' }}>
                  Rules are evaluated by priority (highest first). <strong>DENY</strong> rules block access; <strong>ALLOW</strong> rules grant access. 
                  First matching rule wins. Country restrictions apply only to MAICPP ESI partners; JumpStart partners are exempt.
                </p>
              </div>
              <button
                onClick={() => openRuleModal()}
                data-testid="add-rule-btn"
                style={{
                  padding: '10px 18px', backgroundColor: '#1B5E5F', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'
                }}
              >
                + Add Rule
              </button>
            </div>

            {accessRulesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading...</div>
            ) : accessRules.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                <Shield size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ margin: 0 }}>No DB access rules yet. The hardcoded rules are still active.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                      {['Priority', 'Name', 'Action', 'Aggregation', 'Conditions', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {accessRules.map(rule => (
                      <tr key={rule.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`rule-row-${rule.id}`}>
                        <td style={{ padding: '12px', color: '#6B7280', fontWeight: '600', fontSize: '13px' }}>{rule.priority}</td>
                        <td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>
                          {rule.name}
                          {rule.description && <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{rule.description}</div>}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '700',
                            backgroundColor: rule.action === 'allow' ? '#D1FAE5' : '#FEE2E2',
                            color: rule.action === 'allow' ? '#065F46' : '#991B1B',
                          }}>
                            {rule.action.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px', textTransform: 'uppercase' }}>{rule.aggregation}</td>
                        <td style={{ padding: '12px', fontSize: '12px', color: '#374151', maxWidth: '240px' }}>
                          {rule.conditions && rule.conditions.map((c, i) => (
                            <div key={i} style={{ marginBottom: '2px' }}>
                              <code style={{ background: '#F3F4F6', padding: '1px 4px', borderRadius: '3px', fontSize: '11px' }}>
                                {c.field} {c.operator.replace(/_/g, ' ')} "{c.value}"
                              </code>
                            </div>
                          ))}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '9999px', fontSize: '12px',
                            backgroundColor: rule.is_active ? '#D1FAE5' : '#F3F4F6',
                            color: rule.is_active ? '#065F46' : '#6B7280',
                          }}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => openRuleModal(rule)}
                              data-testid={`edit-rule-btn-${rule.id}`}
                              style={{ padding: '4px 10px', background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteRule(rule.id, rule.name)}
                              data-testid={`delete-rule-btn-${rule.id}`}
                              style={{ padding: '4px 10px', background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div data-testid="blocked-countries-card" style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2C3E50', marginBottom: '4px' }}>Blocked Countries Reference</h3>
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '16px' }}>
              Country restrictions are now enforced via DB access rules. The rule "Block MAICPP ESI from restricted countries" uses this list. Edit that rule to modify the blocked countries.
            </p>
            {blockedCountriesLoading ? (
              <p style={{ fontSize: '13px', color: '#9CA3AF' }}>Loading...</p>
            ) : (
              <div data-testid="blocked-countries-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {blockedCountries.map((country) => (
                  <span
                    key={country}
                    data-testid={`blocked-country-${country.replace(/\s+/g, '-').toLowerCase()}`}
                    style={{
                      display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600',
                      backgroundColor: '#FEE2E2', color: '#991B1B',
                    }}
                  >
                    {country}
                  </span>
                ))}
              </div>
            )}
            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '12px' }}>
              {blockedCountries.length} countries in reference list. <strong>Note:</strong> JumpStart partners are exempt from country restrictions.
            </p>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2C3E50', marginBottom: '4px' }}>Test Access Rules</h3>
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '20px' }}>Enter a user's attributes to simulate which DB access rule applies AND which roles would be assigned by the Role Mapping rules.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              {[
                { key: 'email', label: 'Email', placeholder: 'e.g. user@partner.com' },
                { key: 'email', label: 'Email', placeholder: 'e.g. user@partner.com' },
                { key: 'domain', label: 'Email Domain', placeholder: 'Auto-filled from email' },
                { key: 'domain_type', label: 'Domain Type', placeholder: 'e.g. MAICPP ESI' },
                { key: 'country', label: 'Country', placeholder: 'e.g. India' },
                { key: 'levelup_role', label: 'LevelUp Role', placeholder: 'e.g. Administrator' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>{label}</label>
                  <input
                    type="text"
                    value={testInput[key]}
                    onChange={e => {
                      const val = e.target.value;
                      if (key === 'email') {
                        const derived = val.includes('@') ? val.split('@')[1] : '';
                        setTestInput(prev => ({ ...prev, email: val, domain: derived || prev.domain }));
                      } else {
                        setTestInput(prev => ({ ...prev, [key]: val }));
                      }
                    }}
                    placeholder={placeholder}
                    data-testid={`test-input-${key}`}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={handleTestAccess}
              disabled={testLoading}
              data-testid="test-access-btn"
              style={{
                padding: '10px 20px', backgroundColor: '#4F46E5', color: 'white',
                border: 'none', borderRadius: '6px', cursor: testLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600', fontSize: '14px', opacity: testLoading ? 0.7 : 1
              }}
            >
              {testLoading ? 'Testing...' : 'Test Access'}
            </button>

            {testResult && (
              <div style={{ marginTop: '20px' }}>
                {/* Final Outcome Banner */}
                <div
                  data-testid="test-final-outcome"
                  style={{
                    padding: '16px 20px', borderRadius: '8px', marginBottom: '16px',
                    backgroundColor: testResult.final_outcome?.includes('ALLOWED') ? '#D1FAE5' : '#FEE2E2',
                    border: `2px solid ${testResult.final_outcome?.includes('ALLOWED') ? '#10B981' : '#EF4444'}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {testResult.final_outcome?.includes('ALLOWED') 
                      ? <CheckCircle size={24} color="#059669" />
                      : <AlertCircle size={24} color="#DC2626" />
                    }
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: testResult.final_outcome?.includes('ALLOWED') ? '#065F46' : '#991B1B' }}>
                        {testResult.final_outcome}
                      </div>
                      {testResult.is_exempt_domain && (
                        <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px' }}>
                          Exempt domains (microsoft.com, technofocus.in/co) bypass all access rules
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Access Rule Evaluation Details */}
                <div
                  data-testid="test-result"
                  style={{
                    padding: '16px', borderRadius: '8px', border: '1px solid #E2E8F0',
                    backgroundColor: '#F8FAFC', marginBottom: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <Shield size={18} color="#4F46E5" />
                    <span style={{ fontWeight: '700', fontSize: '14px', color: '#374151' }}>
                      Rule Evaluation Details
                    </span>
                  </div>
                  
                  {/* Normalized Input */}
                  {testResult.input && (
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px', padding: '8px', backgroundColor: '#F1F5F9', borderRadius: '4px' }}>
                      <strong>Evaluated with:</strong> Domain Type = "{testResult.input.domain_type || '(empty)'}" | Country = "{testResult.input.country || '(empty)'}" | Email Domain = "{testResult.input.domain || '(empty)'}"
                    </div>
                  )}

                  {testResult.evaluations && testResult.evaluations.length > 0 && (
                    <div style={{ fontSize: '13px' }}>
                      {testResult.evaluations.map((ev, i) => (
                        <div 
                          key={i} 
                          style={{ 
                            padding: '10px 12px', 
                            marginBottom: '8px', 
                            borderRadius: '6px',
                            backgroundColor: ev.matched ? (ev.action === 'deny' ? '#FEF2F2' : '#F0FDF4') : 'white',
                            border: `1px solid ${ev.matched ? (ev.action === 'deny' ? '#FECACA' : '#86EFAC') : '#E5E7EB'}`,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <span style={{ fontWeight: '600', color: ev.matched ? (ev.action === 'deny' ? '#B91C1C' : '#15803D') : '#6B7280' }}>
                                {ev.matched ? '✓' : '✗'} {ev.rule_name}
                              </span>
                              <span style={{ 
                                marginLeft: '8px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: ev.action === 'allow' ? '#D1FAE5' : '#FEE2E2',
                                color: ev.action === 'allow' ? '#065F46' : '#991B1B'
                              }}>
                                {ev.action.toUpperCase()}
                              </span>
                              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9CA3AF' }}>
                                Priority: {ev.priority}
                              </span>
                            </div>
                            <span style={{ fontSize: '11px', color: '#6B7280' }}>
                              {ev.conditions_met ?? 0}/{ev.conditions_total ?? 0} conditions
                            </span>
                          </div>
                          
                          {/* Show condition details for matched or close-to-matched rules */}
                          {ev.conditions_detail && ev.conditions_detail.length > 0 && (
                            <div style={{ marginTop: '8px', paddingLeft: '16px', borderLeft: '2px solid #E5E7EB' }}>
                              {ev.conditions_detail.map((cd, ci) => (
                                <div key={ci} style={{ fontSize: '11px', color: cd.matched ? '#059669' : '#6B7280', padding: '2px 0' }}>
                                  {cd.matched ? '✓' : '✗'} {cd.field} {cd.operator} "{cd.expected_value?.length > 50 ? cd.expected_value.slice(0,50) + '...' : cd.expected_value}" 
                                  <span style={{ color: '#9CA3AF' }}> (actual: "{cd.actual_value || '(empty)'}")</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Role Assignment Result */}
                <div
                  data-testid="test-role-result"
                  style={{
                    padding: '16px', borderRadius: '8px', border: '1px solid',
                    backgroundColor: testResult.assigned_roles && testResult.assigned_roles.length > 0 ? '#EFF6FF' : '#F8FAFC',
                    borderColor: testResult.assigned_roles && testResult.assigned_roles.length > 0 ? '#BFDBFE' : '#E2E8F0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <Shield size={18} color={testResult.assigned_roles && testResult.assigned_roles.length > 0 ? '#2563EB' : '#9CA3AF'} />
                    <span style={{ fontWeight: '700', fontSize: '15px', color: testResult.assigned_roles && testResult.assigned_roles.length > 0 ? '#1D4ED8' : '#374151' }}>
                      Role Assignment Simulation
                    </span>
                  </div>
                  {testResult.assigned_roles && testResult.assigned_roles.length > 0 ? (
                    <div>
                      <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 8px 0' }}>
                        Matched rule: <strong>{testResult.role_mapping_matched_rule}</strong>
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {testResult.assigned_roles.map(role => (
                          <span
                            key={role}
                            data-testid={`assigned-role-badge-${role}`}
                            style={{
                              padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                              backgroundColor: role === 'admin' ? '#FEF3C7' : role === 'content_owner' ? '#ECFDF5' : role === 'report_viewer' ? '#F5F3FF' : '#EFF6FF',
                              color: role === 'admin' ? '#92400E' : role === 'content_owner' ? '#065F46' : role === 'report_viewer' ? '#5B21B6' : '#1E40AF',
                              border: '1px solid',
                              borderColor: role === 'admin' ? '#FDE68A' : role === 'content_owner' ? '#6EE7B7' : role === 'report_viewer' ? '#DDD6FE' : '#BFDBFE',
                            }}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>No role mapping rules matched for these attributes.</p>
                  )}
                  {testResult.role_mapping_evaluations && testResult.role_mapping_evaluations.length > 0 && (
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', margin: '8px 0 4px' }}>Role mapping evaluations:</p>
                      {testResult.role_mapping_evaluations.map((ev, i) => (
                        <div key={i} style={{ fontSize: '12px', color: ev.matched ? '#1D4ED8' : '#6B7280', padding: '2px 0' }}>
                          {ev.matched ? '✓' : '✗'} {ev.rule_name} (P{ev.priority}) {ev.is_system ? '[System]' : '[Custom]'}{ev.matched ? ` → ${ev.assigned_roles.join(', ')}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
            </>
          )}

          {/* ── Role Mapping Sub-tab ── */}
          {accessSubTab === 'role-mapping' && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2C3E50', margin: '0 0 4px 0' }}>Role Mappings</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', maxWidth: '600px', lineHeight: 1.4 }}>
                  Defines which portal roles are automatically assigned to SSO users on login, based on their email domain, LevelUp domain type, or LevelUp role.
                  <strong style={{ color: '#0F4C75' }}> System</strong> rules mirror the current hardcoded logic and are read-only.
                  Custom rules can be added to override or extend the defaults.
                </p>
              </div>
              <button
                onClick={openAddRoleMapping}
                data-testid="add-role-mapping-btn"
                style={{
                  padding: '9px 16px', backgroundColor: '#0F4C75', color: 'white',
                  border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                  display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', marginLeft: '16px'
                }}
              >
                <Plus size={15} /> Add Rule
              </button>
            </div>

            <div style={{ marginBottom: '14px', padding: '10px 14px', backgroundColor: '#EFF6FF', borderRadius: '6px', borderLeft: '3px solid #3B82F6', fontSize: '12px', color: '#1E40AF' }}>
              <strong>Note:</strong> These rules are currently for reference and planning only. The hardcoded SSO logic remains active to ensure portal stability. Future releases will allow these rules to enforce role assignments directly.
            </div>

            {roleMappingLoading ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#9CA3AF' }}>Loading...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                      {['Priority', 'Name', 'Match Condition', 'And (Optional)', 'Assigned Roles', 'Type', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {roleMappingRules.map(rule => (
                      <tr key={rule.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: rule.is_active ? 1 : 0.55 }} data-testid={`role-mapping-row-${rule.id}`}>
                        <td style={{ padding: '12px', fontWeight: '700', color: '#374151', fontSize: '13px' }}>{rule.priority}</td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '600', color: '#111827' }}>{rule.name}</div>
                          {rule.description && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px', maxWidth: '180px' }}>{rule.description.slice(0, 70)}{rule.description.length > 70 ? '…' : ''}</div>}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <code style={{ fontSize: '11px', backgroundColor: '#F3F4F6', padding: '3px 7px', borderRadius: '4px', color: '#374151' }}>
                            {rule.match_field === 'any' ? '(any)' : `${rule.match_field} ${rule.match_operator} "${rule.match_value}"`}
                          </code>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {rule.secondary_field ? (
                            <code style={{ fontSize: '11px', backgroundColor: '#EFF6FF', padding: '3px 7px', borderRadius: '4px', color: '#1E40AF' }}>
                              {rule.secondary_field} {rule.secondary_operator} "{rule.secondary_value}"
                            </code>
                          ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {(rule.assigned_roles || []).map(r => (
                              <span key={r} style={{
                                padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: r === 'admin' ? '#FEF3C7' : r === 'content_owner' ? '#DBEAFE' : r === 'report_viewer' ? '#F3E8FF' : '#F3F4F6',
                                color: r === 'admin' ? '#92400E' : r === 'content_owner' ? '#1E40AF' : r === 'report_viewer' ? '#7C3AED' : '#374151',
                              }}>{r}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {rule.is_system ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                              <Lock size={10} /> System
                            </span>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ECFDF5', color: '#065F46' }}>Custom</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {rule.is_system ? (
                            <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#D1FAE5', color: '#065F46' }}>Active</span>
                          ) : (
                            <button
                              onClick={() => handleToggleRoleMapping(rule.id)}
                              data-testid={`toggle-rm-${rule.id}`}
                              style={{
                                padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', border: '1px solid',
                                backgroundColor: rule.is_active ? '#D1FAE5' : '#F3F4F6',
                                color: rule.is_active ? '#065F46' : '#6B7280',
                                borderColor: rule.is_active ? '#A7F3D0' : '#D1D5DB',
                              }}
                            >
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {!rule.is_system && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => openEditRoleMapping(rule)}
                                data-testid={`edit-rm-${rule.id}`}
                                style={{ padding: '5px 10px', backgroundColor: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                              >Edit</button>
                              <button
                                onClick={() => handleDeleteRoleMapping(rule.id, rule.name)}
                                data-testid={`delete-rm-${rule.id}`}
                                style={{ padding: '5px 10px', backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}
                              ><Trash2 size={13} /></button>
                            </div>
                          )}
                          {rule.is_system && <span style={{ color: '#D1D5DB', fontSize: '12px' }}><Lock size={13} /></span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
          )}

          {/* ── SSO Attempts Log Sub-tab (Super Admin Only) ── */}
          {accessSubTab === 'sso-log' && isSuperAdmin && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2C3E50', margin: '0 0 4px 0' }}>SSO Attempts Log</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                  Recent SSO login attempts with full LevelUp token data. Use this to debug access issues.
                </p>
              </div>
              <button
                onClick={fetchSsoAttempts}
                disabled={ssoAttemptsLoading}
                data-testid="refresh-sso-log-btn"
                style={{
                  padding: '8px 14px', backgroundColor: '#1B5E5F', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: ssoAttemptsLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
                  opacity: ssoAttemptsLoading ? 0.7 : 1
                }}
              >
                <RefreshCw size={14} style={{ animation: ssoAttemptsLoading ? 'spin 1s linear infinite' : 'none' }} />
                Refresh
              </button>
            </div>

            {/* Search and Filter */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search by email, domain, or name..."
                value={ssoSearchQuery}
                onChange={(e) => setSsoSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchSsoAttempts()}
                data-testid="sso-search-input"
                style={{
                  flex: 1, padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '6px',
                  fontSize: '14px', outline: 'none'
                }}
              />
              <select
                value={ssoResultFilter}
                onChange={(e) => setSsoResultFilter(e.target.value)}
                data-testid="sso-result-filter"
                style={{
                  padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '6px',
                  fontSize: '14px', outline: 'none', minWidth: '130px'
                }}
              >
                <option value="">All Results</option>
                <option value="allowed">Allowed</option>
                <option value="denied">Denied</option>
              </select>
              <button
                onClick={fetchSsoAttempts}
                data-testid="sso-search-btn"
                style={{
                  padding: '10px 16px', backgroundColor: '#374151', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                }}
              >
                Search
              </button>
            </div>

            {ssoAttemptsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading SSO attempts...</div>
            ) : ssoAttempts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                <AlertCircle size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ margin: 0 }}>No SSO attempts found. Login attempts will appear here once users start logging in.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                      {['Time', 'Email / Name', 'Domain Type', 'Country', 'Result', 'Reason / Rule'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ssoAttempts.map(attempt => (
                      <tr key={attempt.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`sso-attempt-row-${attempt.id}`}>
                        <td style={{ padding: '10px 12px', color: '#6B7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {attempt.created_at ? new Date(attempt.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ fontWeight: '500', color: '#111827' }}>{attempt.email}</div>
                          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{attempt.full_name}</div>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#374151' }}>
                          <code style={{ padding: '2px 6px', backgroundColor: '#F3F4F6', borderRadius: '4px', fontSize: '12px' }}>
                            {attempt.domain_type || '(none)'}
                          </code>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#374151' }}>{attempt.country || '—'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '700',
                            backgroundColor: attempt.result === 'allowed' ? '#D1FAE5' : '#FEE2E2',
                            color: attempt.result === 'allowed' ? '#065F46' : '#991B1B',
                          }}>
                            {attempt.result.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6B7280', maxWidth: '300px' }}>
                          <div>{attempt.reason || '—'}</div>
                          {attempt.matched_rule && (
                            <div style={{ marginTop: '2px', fontSize: '11px', color: '#9CA3AF' }}>Rule: {attempt.matched_rule}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '6px', fontSize: '12px', color: '#92400E', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} />
              <span><strong>Note:</strong> This log contains sensitive SSO token data. Access is restricted to Super Admins only.</span>
            </div>
          </div>
          )}
        </div>
      )}


      {/* ===== API DOCS TAB ===== */}
      {activeTab === 'api-docs' && (() => {
        const copyCode = (id, text) => {
          navigator.clipboard.writeText(text).then(() => {
            setCopiedBlock(id);
            setTimeout(() => setCopiedBlock(null), 2000);
          });
        };

        const PROD_BASE = 'https://compete.skillupwithlevelup.com/api';

        const PARAMS_DOC = [
          { name: 'time_range', type: 'string', default: 'all', desc: '"all" | "last_week" (7 days) | "last_month" (30 days)' },
          { name: 'fiscal_year', type: 'string', default: '—', desc: '"FY24" | "FY25" | "FY26" | "FY27" — overrides time_range' },
          { name: 'date_from', type: 'YYYY-MM-DD', default: '—', desc: 'Start date (inclusive) — overrides fiscal_year & time_range' },
          { name: 'date_to', type: 'YYYY-MM-DD', default: '—', desc: 'End date (inclusive) — overrides fiscal_year & time_range' },
        ];

        const ENDPOINTS = [
          {
            id: 'overview',
            path: '/reports/overview',
            desc: 'Top-level KPIs (assets, downloads, users, active partners), daily download trend time-series, and top 20 downloaded assets.',
            responseExample: `{
  "success": true,
  "filters_applied": { "time_range": "all", "fiscal_year": null, "date_from": null, "date_to": null },
  "summary": {
    "total_assets": 81,
    "total_downloads": 1266,
    "total_users": 485,
    "active_partners": 292
  },
  "download_trend": [
    { "date": "2026-03-13", "downloads": 14 },
    { "date": "2026-03-14", "downloads": 8 }
  ],
  "top_assets": [
    { "id": "...", "title": "Claude vs Copilot Battlecard", "solution_area": "AI Workforce", "content_type": "Battlecard", "downloads": 42 }
  ]
}`,
          },
          {
            id: 'content-analytics',
            path: '/reports/content-analytics',
            desc: 'Asset counts and download counts broken down by Solution Area, Content Type, and Language.',
            responseExample: `{
  "success": true,
  "by_solution_area": [
    { "solution_area": "AI Workforce", "asset_count": 78, "download_count": 506 }
  ],
  "by_content_type": [
    { "content_type": "Presentation", "asset_count": 90, "download_count": 380 }
  ],
  "by_language": [
    { "language": "English", "asset_count": 75, "download_count": 340 }
  ]
}`,
          },
          {
            id: 'download-analytics',
            path: '/reports/download-analytics',
            desc: 'Downloads broken down by Solution Area + Play combination (with unique user counts), and by Country.',
            responseExample: `{
  "success": true,
  "by_solution_play": [
    { "solution_area": "AI Workforce", "solution_play": "Copilot and Agents at Work", "downloads": 506, "unique_users": 114 }
  ],
  "by_country": [
    { "country": "United States", "downloads": 160 },
    { "country": "Germany", "downloads": 85 }
  ]
}`,
          },
          {
            id: 'partner-analytics',
            path: '/reports/partner-analytics',
            desc: 'Downloads grouped by partner organisation domain. Returns top 50 domains ordered by total downloads descending.',
            responseExample: `{
  "success": true,
  "by_organization": [
    { "domain": "microsoft.com", "total_downloads": 78, "unique_users": 28 },
    { "domain": "ingrammicro.com", "total_downloads": 24, "unique_users": 5 }
  ]
}`,
          },
          {
            id: 'downloads-raw',
            path: '/reports/downloads-raw',
            desc: 'Row-level (raw) download events feed — one row per individual download event. Additive endpoint for the MAQ Insights Cube feed; does not affect the 4 aggregated analytics endpoints above.',
            customParams: [
              { name: 'date_from', type: 'YYYY-MM-DD', default: '—', desc: 'Filters on the download event timestamp (inclusive)' },
              { name: 'date_to', type: 'YYYY-MM-DD', default: '—', desc: 'Filters on the download event timestamp (inclusive)' },
              { name: 'page', type: 'integer', default: '1', desc: 'Page number' },
              { name: 'per_page', type: 'integer', default: '100', desc: 'Rows per page, max 500' },
            ],
            responseExample: `{
  "success": true,
  "total": 1994,
  "per_page": 100,
  "current_page": 1,
  "last_page": 20,
  "data": [
    {
      "id": "10eff24b-05c1-4d5c-87a0-bc8a6a1c558d",
      "asset_id": "6ede037e-987c-47ea-af3a-ce29e34a3850",
      "asset_title": "Claude Cowork vs. Copilot Cowork Side-by-Side Comparisons",
      "solution_area": "AI Workforce",
      "content_type": "Presentation",
      "language": "English",
      "user_name": "Alexandra Barry",
      "user_email": "albarry@microsoft.com",
      "lms_id": 4587231,
      "organization_domain": "microsoft.com",
      "country": "United States",
      "downloaded_at": "2026-09-01 23:39:06"
    }
  ]
}`,
          },
        ];

        const codeStyle = {
          backgroundColor: '#0F172A', color: '#E2E8F0', fontFamily: "'Courier New', Courier, monospace",
          fontSize: '12.5px', lineHeight: '1.65', borderRadius: '8px', padding: '16px 20px',
          whiteSpace: 'pre', overflowX: 'auto', margin: 0,
        };

        const getBadge = (text, bg, color) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '5px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', backgroundColor: bg, color }}>{text}</span>
        );

        return (
          <div data-testid="api-docs-tab" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Header card */}
            <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '28px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: '4px solid #1B5E5F' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <Code2 size={22} color="#1B5E5F" />
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1B5E5F', margin: 0 }}>LevelUp Reporting API</h2>
                <span style={{ backgroundColor: '#D1FAE5', color: '#065F46', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '9999px' }}>5 Endpoints</span>
              </div>
              <p style={{ fontSize: '14px', color: '#4B5563', margin: '0 0 20px', lineHeight: '1.6' }}>
                Read-only server-to-server API for the LevelUp Reporting portal. The first 4 endpoints share the same optional date filter query parameters; the 5th (raw downloads feed) uses its own pagination params. All return JSON.
              </p>

              {/* Base URL */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Globe size={14} color="#6B7280" />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Base URL</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <pre style={{ ...codeStyle, padding: '12px 48px 12px 16px' }}>{PROD_BASE}</pre>
                  <button
                    data-testid="copy-base-url"
                    onClick={() => copyCode('base-url', PROD_BASE)}
                    title="Copy"
                    style={{ position: 'absolute', top: '8px', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: copiedBlock === 'base-url' ? '#34D399' : '#94A3B8', padding: '2px' }}
                  >
                    {copiedBlock === 'base-url' ? <Check size={15} /> : <Copy size={15} />}
                  </button>
                </div>
              </div>

              {/* Auth */}
              <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px', padding: '14px 18px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Key size={16} color="#C2410C" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: '#7C2D12' }}>Authentication Required</p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#9A3412' }}>
                    Every request must include the header <code style={{ backgroundColor: '#FEE2E2', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>X-Report-Api-Key: {'<secret>'}</code>.
                    The key is shared directly with the LevelUp backend team — it is not displayed here.
                    Requests without a valid key return <code style={{ backgroundColor: '#FEE2E2', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>401 Unauthorized</code>.
                  </p>
                </div>
              </div>
            </div>

            {/* Shared query params */}
            <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '24px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#2C3E50', margin: '0 0 14px' }}>Shared Query Parameters</h3>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 14px' }}>The first 4 endpoints (Overview, Content Analytics, Download Analytics, Partner Analytics) accept the same optional filter parameters below. If no filter is provided, all-time data is returned. The 5th endpoint (Raw Downloads Feed) uses its own params — see its card below.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                      {['Parameter', 'Type', 'Default', 'Description'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: '#374151' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PARAMS_DOC.map((p, i) => (
                      <tr key={p.name} style={{ borderBottom: '1px solid #F1F5F9', backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                        <td style={{ padding: '10px 14px' }}><code style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', padding: '2px 7px', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }}>{p.name}</code></td>
                        <td style={{ padding: '10px 14px', color: '#6B7280', fontFamily: 'monospace', fontSize: '12px' }}>{p.type}</td>
                        <td style={{ padding: '10px 14px', color: '#6B7280' }}>{p.default}</td>
                        <td style={{ padding: '10px 14px', color: '#374151' }}>{p.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Endpoint cards */}
            {ENDPOINTS.map((ep, idx) => (
              <div key={ep.id} data-testid={`api-endpoint-${ep.id}`} style={{ backgroundColor: 'white', borderRadius: '10px', padding: '24px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280' }}>#{idx + 1}</span>
                  {getBadge('GET', '#DCFCE7', '#15803D')}
                  <code style={{ fontSize: '15px', fontWeight: '700', color: '#1B5E5F', fontFamily: 'monospace' }}>{ep.path}</code>
                </div>
                <p style={{ fontSize: '13px', color: '#4B5563', margin: '0 0 18px', lineHeight: '1.6' }}>{ep.desc}</p>

                {ep.customParams && (
                  <div style={{ marginBottom: '18px', overflowX: 'auto' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Query Parameters</span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                          {['Parameter', 'Type', 'Default', 'Description'].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: '#374151' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ep.customParams.map((p, i) => (
                          <tr key={p.name} style={{ borderBottom: '1px solid #F1F5F9', backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                            <td style={{ padding: '10px 14px' }}><code style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', padding: '2px 7px', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }}>{p.name}</code></td>
                            <td style={{ padding: '10px 14px', color: '#6B7280', fontFamily: 'monospace', fontSize: '12px' }}>{p.type}</td>
                            <td style={{ padding: '10px 14px', color: '#6B7280' }}>{p.default}</td>
                            <td style={{ padding: '10px 14px', color: '#374151' }}>{p.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Example Response</span>
                  <button
                    data-testid={`copy-response-${ep.id}`}
                    onClick={() => copyCode(ep.id, ep.responseExample)}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', backgroundColor: copiedBlock === ep.id ? '#D1FAE5' : '#F1F5F9', color: copiedBlock === ep.id ? '#065F46' : '#475569', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                  >
                    {copiedBlock === ep.id ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                </div>
                <pre style={codeStyle}>{ep.responseExample}</pre>
              </div>
            ))}
          </div>
        );
      })()}


      {/* Role Mapping Rule Modal */}
      {roleMappingModalOpen && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setRoleMappingModalOpen(false); }}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: '16px' }}
        >
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0F4C75', marginBottom: '20px' }}>
              {editingRoleMapping ? 'Edit Role Mapping Rule' : 'Add Role Mapping Rule'}
            </h3>

            {/* Name */}
            <div style={{ marginBottom: '14px' }}>
              <label style={rmLabelStyle}>Rule Name *</label>
              <input type="text" value={roleMappingForm.name} onChange={e => setRoleMappingForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Allow Resellers as Partners" data-testid="rm-name-input"
                style={rmInputStyle} />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '14px' }}>
              <label style={rmLabelStyle}>Description</label>
              <input type="text" value={roleMappingForm.description} onChange={e => setRoleMappingForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description" style={rmInputStyle} />
            </div>

            {/* Priority */}
            <div style={{ marginBottom: '14px' }}>
              <label style={rmLabelStyle}>Priority <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(higher = evaluated first)</span></label>
              <input type="number" value={roleMappingForm.priority} onChange={e => setRoleMappingForm(p => ({ ...p, priority: Number(e.target.value) }))}
                min={1} max={999} data-testid="rm-priority-input"
                style={{ ...rmInputStyle, width: '100px' }} />
            </div>

            {/* Primary Condition */}
            <div style={{ marginBottom: '14px', padding: '14px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <label style={{ ...rmLabelStyle, marginBottom: '10px' }}>Match Condition (Primary) *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: '8px' }}>
                <select value={roleMappingForm.match_field} onChange={e => setRoleMappingForm(p => ({ ...p, match_field: e.target.value }))}
                  data-testid="rm-match-field" style={rmSelectStyle}>
                  <option value="email_domain">Email Domain</option>
                  <option value="domain_type">LevelUp Domain Type</option>
                  <option value="levelup_role">LevelUp Role</option>
                  <option value="any">Any (catch-all)</option>
                </select>
                <select value={roleMappingForm.match_operator} onChange={e => setRoleMappingForm(p => ({ ...p, match_operator: e.target.value }))}
                  disabled={roleMappingForm.match_field === 'any'} style={rmSelectStyle}>
                  <option value="equals">equals</option>
                  <option value="in">in list (comma-sep)</option>
                  <option value="contains">contains</option>
                  <option value="starts_with">starts with</option>
                </select>
                <input type="text" value={roleMappingForm.match_value} onChange={e => setRoleMappingForm(p => ({ ...p, match_value: e.target.value }))}
                  placeholder={roleMappingForm.match_field === 'any' ? '(any)' : 'e.g. microsoft.com'}
                  disabled={roleMappingForm.match_field === 'any'}
                  style={rmInputStyle} />
              </div>
            </div>

            {/* Secondary Condition (AND) */}
            <div style={{ marginBottom: '14px', padding: '14px', backgroundColor: '#F0F9FF', borderRadius: '8px', border: '1px solid #BAE6FD' }}>
              <label style={{ ...rmLabelStyle, marginBottom: '10px' }}>AND Condition <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span></label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: '8px' }}>
                <select value={roleMappingForm.secondary_field} onChange={e => setRoleMappingForm(p => ({ ...p, secondary_field: e.target.value }))}
                  style={rmSelectStyle}>
                  <option value="">— None —</option>
                  <option value="email_domain">Email Domain</option>
                  <option value="domain_type">LevelUp Domain Type</option>
                  <option value="levelup_role">LevelUp Role</option>
                </select>
                <select value={roleMappingForm.secondary_operator} onChange={e => setRoleMappingForm(p => ({ ...p, secondary_operator: e.target.value }))}
                  disabled={!roleMappingForm.secondary_field} style={rmSelectStyle}>
                  <option value="equals">equals</option>
                  <option value="in">in list</option>
                  <option value="contains">contains</option>
                </select>
                <input type="text" value={roleMappingForm.secondary_value} onChange={e => setRoleMappingForm(p => ({ ...p, secondary_value: e.target.value }))}
                  placeholder="e.g. Administrator"
                  disabled={!roleMappingForm.secondary_field}
                  style={rmInputStyle} />
              </div>
            </div>

            {/* Assigned Roles */}
            <div style={{ marginBottom: '14px' }}>
              <label style={rmLabelStyle}>Assign These Roles * <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(select all that apply)</span></label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {['partner', 'content_owner', 'admin', 'report_viewer'].map(role => (
                  <label key={role} data-testid={`rm-role-${role}`} style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
                    borderRadius: '7px', border: '1.5px solid',
                    borderColor: roleMappingForm.assigned_roles.includes(role) ? '#0F4C75' : '#D1D5DB',
                    backgroundColor: roleMappingForm.assigned_roles.includes(role) ? '#EFF6FF' : 'white',
                    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                    color: roleMappingForm.assigned_roles.includes(role) ? '#0F4C75' : '#6B7280',
                  }}>
                    <input type="checkbox" checked={roleMappingForm.assigned_roles.includes(role)}
                      onChange={() => toggleRoleMappingRole(role)} style={{ margin: 0 }} />
                    {role}
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '14px' }}>
              <label style={rmLabelStyle}>Notes <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(internal, not shown to users)</span></label>
              <textarea value={roleMappingForm.notes} onChange={e => setRoleMappingForm(p => ({ ...p, notes: e.target.value }))}
                rows={2} placeholder="Any extra context or rationale..."
                style={{ ...rmInputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            {/* Active toggle */}
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" id="rm-active" checked={roleMappingForm.is_active}
                onChange={e => setRoleMappingForm(p => ({ ...p, is_active: e.target.checked }))} />
              <label htmlFor="rm-active" style={{ fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>Rule is Active</label>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
              <button onClick={() => setRoleMappingModalOpen(false)}
                style={{ padding: '10px 20px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
              <button onClick={handleSaveRoleMapping} data-testid="save-role-mapping-btn"
                style={{ padding: '10px 24px', backgroundColor: '#0F4C75', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                {editingRoleMapping ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
      {ruleModalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '16px'
          }}
          onClick={e => { if (e.target === e.currentTarget) setRuleModalOpen(false); }}
        >
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1B5E5F', marginBottom: '20px' }}>
              {editingRule ? 'Edit Access Rule' : 'Create Access Rule'}
            </h3>

            {/* Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Rule Name *</label>
              <input
                type="text"
                value={ruleForm.name}
                onChange={e => setRuleForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Block China"
                data-testid="rule-name-input"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Description</label>
              <input
                type="text"
                value={ruleForm.description}
                onChange={e => setRuleForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                data-testid="rule-description-input"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Action + Aggregation + Priority row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Action *</label>
                <select
                  value={ruleForm.action}
                  onChange={e => setRuleForm(p => ({ ...p, action: e.target.value }))}
                  data-testid="rule-action-select"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                >
                  <option value="deny">DENY</option>
                  <option value="allow">ALLOW</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Aggregation</label>
                <select
                  value={ruleForm.aggregation}
                  onChange={e => setRuleForm(p => ({ ...p, aggregation: e.target.value }))}
                  data-testid="rule-aggregation-select"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                >
                  <option value="and">AND (all must match)</option>
                  <option value="or">OR (any must match)</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Priority</label>
                <input
                  type="number"
                  value={ruleForm.priority}
                  onChange={e => setRuleForm(p => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
                  data-testid="rule-priority-input"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Active toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <input
                type="checkbox"
                id="rule-active"
                checked={ruleForm.is_active}
                onChange={e => setRuleForm(p => ({ ...p, is_active: e.target.checked }))}
                data-testid="rule-active-checkbox"
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <label htmlFor="rule-active" style={{ fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>Rule is active</label>
            </div>

            {/* Conditions */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Conditions</label>
                <button
                  onClick={() => setRuleForm(p => ({
                    ...p,
                    conditions: [...p.conditions, { _key: Date.now(), field: 'country', operator: 'equals', value: '' }]
                  }))}
                  data-testid="add-condition-btn"
                  style={{ padding: '4px 12px', background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                >
                  + Add Condition
                </button>
              </div>
              {ruleForm.conditions.map((cond, i) => (
                <div key={cond._key || i} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.5fr auto', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={cond.field}
                      onChange={e => setRuleForm(p => ({ ...p, conditions: p.conditions.map((c, ci) => ci === i ? { ...c, field: e.target.value } : c) }))}
                      data-testid={`cond-field-${i}`}
                      style={{ padding: '7px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                    >
                      <option value="country">Country</option>
                      <option value="domain">Email Domain</option>
                      <option value="domain_type">Domain Type</option>
                      <option value="email">Email</option>
                    </select>
                    <select
                      value={cond.operator}
                      onChange={e => setRuleForm(p => ({ ...p, conditions: p.conditions.map((c, ci) => ci === i ? { ...c, operator: e.target.value } : c) }))}
                      data-testid={`cond-operator-${i}`}
                      style={{ padding: '7px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                    >
                      <option value="equals">equals</option>
                      <option value="not_equals">not equals</option>
                      <option value="contains">contains</option>
                      <option value="starts_with">starts with</option>
                      <option value="ends_with">ends with</option>
                      <option value="in_list">in list (comma-sep)</option>
                      <option value="not_in_list">not in list</option>
                      <option value="not_contains_any">not contains any (comma-sep)</option>
                      <option value="contains_without">contains X but not Y (X|Y)</option>
                    </select>
                    <input
                      type="text"
                      value={cond.value}
                      onChange={e => setRuleForm(p => ({ ...p, conditions: p.conditions.map((c, ci) => ci === i ? { ...c, value: e.target.value } : c) }))}
                      placeholder="Value"
                      data-testid={`cond-value-${i}`}
                      style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                    />
                    <button
                      onClick={() => setRuleForm(p => ({ ...p, conditions: p.conditions.filter((_, ci) => ci !== i) }))}
                      disabled={ruleForm.conditions.length <= 1}
                      data-testid={`remove-cond-${i}`}
                      style={{
                        padding: '6px 8px', background: '#FEF2F2', color: '#DC2626',
                        border: '1px solid #FECACA', borderRadius: '6px', cursor: ruleForm.conditions.length <= 1 ? 'not-allowed' : 'pointer',
                        opacity: ruleForm.conditions.length <= 1 ? 0.4 : 1
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {/* Helper button for inserting blocked countries list */}
                  {cond.field === 'country' && (cond.operator === 'in_list' || cond.operator === 'not_in_list') && (
                    <div style={{ marginTop: '6px', paddingLeft: '4px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/blocked-countries`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setRuleForm(p => ({
                                ...p,
                                conditions: p.conditions.map((c, ci) => ci === i ? { ...c, value: data.csv } : c)
                              }));
                            }
                          } catch (err) {
                            console.error('Failed to fetch blocked countries', err);
                          }
                        }}
                        data-testid={`insert-blocked-countries-${i}`}
                        style={{
                          padding: '4px 10px', fontSize: '11px', fontWeight: '500',
                          background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A',
                          borderRadius: '4px', cursor: 'pointer'
                        }}
                      >
                        📋 Insert 28 blocked countries
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Modal footer */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
              <button
                onClick={() => setRuleModalOpen(false)}
                style={{ padding: '10px 20px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRule}
                data-testid="save-rule-btn"
                style={{ padding: '10px 20px', backgroundColor: '#1B5E5F', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>

      <LevelUpFooter />
    </div>
  );
}

export default Admin;

// ── Role Mapping Modal shared styles ──────────────────────────────────────────
const rmLabelStyle = { fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' };
const rmInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const rmSelectStyle = { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', outline: 'none', backgroundColor: 'white' };
